import { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { FROM_DATE_LIMIT } from "./DatePicker.consts";
import DateTimeContext from "../../../contexts/DateTimeContext";
import dayjs from "dayjs";

const usePredefinedDatePicker = (
  dates,
  setDates,
  fromDateLimit,
  allowSearchParams,
  fromKeyword,
  toKeyword,
  testId
) => {
  const { getTimeZoneDate, timeZone } = useContext(DateTimeContext);
  const dateOptions = useMemo(() => [
    {
      key: "today",
      label: "Today",
      startDate: getTimeZoneDate().startOf("d"),
      endDate: getTimeZoneDate().endOf("d"),
      testId: `${testId}_today`
    },
    {
      key: "yesterday",
      label: "Yesterday",
      startDate: getTimeZoneDate().subtract(1, "d").startOf("d"),
      endDate: getTimeZoneDate().subtract(1, "d").endOf("d"),
      testId: `${testId}_yesterday`
    },
    {
      key: "last_7_days",
      label: "Last 7 Days",
      startDate: getTimeZoneDate().subtract(7, "d").startOf("d"),
      endDate: getTimeZoneDate().endOf("d"),
      testId: `${testId}_last_7_days`
    },
    {
      key: "last_14_days",
      label: "Last 14 Days",
      startDate: getTimeZoneDate().subtract(14, "d").startOf("d"),
      endDate: getTimeZoneDate().endOf("d"),
      testId: `${testId}_last_14_days`
    },
    {
      key: "last_30_days",
      label: "Last 30 Days",
      startDate: getTimeZoneDate().subtract(30, "d").startOf("d"),
      endDate: getTimeZoneDate().endOf("d"),
      testId: `${testId}_last_30_days`
    }

  ], [getTimeZoneDate, testId]);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [localDates, setLocalDates] = useState([...dates]);
  const [selectedDate, setSelectedDate] = useState(dateOptions[2]);

  useEffect(() => {
    let tempDates = [...dates];

    if (allowSearchParams) {
      if (searchParams?.get(fromKeyword)) {
        const fromDate = dayjs(searchParams?.get(fromKeyword)).tz(timeZone);
        tempDates = [fromDate, tempDates[1]];
      }

      if (searchParams?.get(toKeyword)) {
        const toDate = dayjs(searchParams?.get(toKeyword)).tz(timeZone);
        tempDates = [tempDates[0], toDate];
      }
    }

    const predefinedDateSelected = dateOptions.find(
      dateOption =>
        tempDates
        && dateOption.startDate.format() === tempDates[0].format()
        && dateOption.endDate.format() === tempDates[1].format()
    );
    setSelectedDate(predefinedDateSelected);

    if ((tempDates[0] && tempDates[0].format() !== dates[0].format())
        || (tempDates[1] && tempDates[1]?.format() !== dates[1].format())) {
      setDates(tempDates[0], tempDates[1]);
      setLocalDates(tempDates);
    }

    if (allowSearchParams && predefinedDateSelected) {
      updateSearchParams(predefinedDateSelected);
    }

  }, [dates, allowSearchParams, dateOptions]);

  const updateSearchParams = (dateOption) => {
    searchParams.set(fromKeyword, dateOption.startDate.format());
    searchParams.set(toKeyword, dateOption.endDate.format());
    setSearchParams(searchParams);
  };

  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const selectDate = (dateOption, closeMenu, isDateInvalid) => {
    if (closeMenu) {
      handleCloseMenu();
    }

    if (!isDateInvalid) {
      if (allowSearchParams) {
        updateSearchParams(dateOption);
      }
      setLocalDates([dateOption.startDate, dateOption.endDate]);
      setDates(dateOption.startDate, dateOption.endDate);
    }
  };

  const shouldDisableFromDate = (date) => {
    if (fromDateLimit === FROM_DATE_LIMIT.ONE_YEAR) {
      return date < getTimeZoneDate().subtract(13, "months");
    }
    return isMoreThanThreeMonths(date);

  };

  const isMoreThanThreeMonths = date =>
    date < getTimeZoneDate().subtract(90, "d");

  const shouldDisableToDate = date =>
    isMoreThanThreeMonths(date)
    || (dates[0] && date.endOf("d").valueOf() < dates[0].startOf("d").valueOf());

  const isMenuOpen = Boolean(menuAnchorEl);

  const isMenuItemSelected = dateOption =>
    selectedDate
    && selectedDate.startDate === dateOption.startDate
    && selectedDate.endDate === dateOption.endDate;

  return {
    dateOptions,
    selectedDate,
    localDates,
    isMenuOpen,
    menuAnchorEl,
    isMenuItemSelected,
    handleOpenMenu,
    handleCloseMenu,
    selectDate,
    shouldDisableFromDate,
    shouldDisableToDate
  };

};

export default usePredefinedDatePicker;
